import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "sonner";
import { AnimatePresence, motion } from "framer-motion";

import Landing from "@/pages/Landing";
import Booking from "@/pages/Booking";
import BookingStatus from "@/pages/BookingStatus";
import AdminConsole from "@/pages/AdminConsole";
import AdminPWA from "@/pages/AdminPWA";
import NotFound from "@/pages/NotFound";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import Contact from "@/pages/Contact";
import ConsentBanner from "@/components/ConsentBanner";

const pageVariants = {
  initial: { opacity: 0, scale: 0.99 },
  in:      { opacity: 1, scale: 1 },
  out:     { opacity: 0, scale: 1.01 },
};

const pageTransition = {
  duration: 0.5,
  ease: [0.16, 1, 0.3, 1],
};

function PageWrapper({ children }) {
  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      style={{ width: "100%", minHeight: "100vh" }}
    >
      {children}
    </motion.div>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route path="/"           element={<PageWrapper><Landing /></PageWrapper>} />
        <Route path="/book"       element={<PageWrapper><Booking /></PageWrapper>} />
        <Route path="/status"     element={<PageWrapper><BookingStatus /></PageWrapper>} />
        <Route path="/adminconsole/*" element={<AdminConsole />} />
        <Route path="/adminpwa/*" element={<AdminPWA />} />
        <Route path="/contact"    element={<PageWrapper><Contact /></PageWrapper>} />
        <Route path="/privacy"    element={<PageWrapper><PrivacyPolicy /></PageWrapper>} />
        <Route path="/terms"      element={<PageWrapper><TermsAndConditions /></PageWrapper>} />
        <Route path="*"           element={<PageWrapper><NotFound /></PageWrapper>} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AnimatedRoutes />
            <ConsentBanner />
          </BrowserRouter>
          <Toaster
            theme="system"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "var(--bg-elevated)",
                border: "1px solid var(--stroke-strong)",
                color: "var(--text)",
                fontFamily: "'Geist', sans-serif",
              },
            }}
          />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
